import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SessionProvider, useSession } from './lib/SessionContext';
import { signOut } from './lib/auth';
import Layout from './components/Layout';
import { FullPageSpinner, Alert } from './components/ui';
import { t } from './i18n';

import LoginView from './views/LoginView';
import RegisterView from './views/RegisterView';
import HomeView from './views/HomeView';
import MasterDataView from './views/MasterDataView';
import AgendaView from './views/AgendaView';
import CheckInView from './views/CheckInView';
import BookingView from './views/BookingView';
import PaymentsView from './views/PaymentsView';
import CashFlowView from './views/CashFlowView';
import ClinicalHistoryView from './views/ClinicalHistoryView';
import ApprovalsView from './views/ApprovalsView';
import PatientsView from './views/PatientsView';
import ProfessionalsView from './views/ProfessionalsView';
import SubscriptionsView from './views/SubscriptionsView';
import LocationsView from './views/LocationsView';
import ActivateProfessionalView from './views/ActivateProfessionalView';
import ForgotPasswordView from './views/ForgotPasswordView';
import ProfessionalSignupView from './views/ProfessionalSignupView';
import ProfileView from './views/ProfileView';
import ResetPasswordView from './views/ResetPasswordView';
import MyPaymentsView from './views/MyPaymentsView';
import BillingSummaryView from './views/BillingSummaryView';
import SettingsView from './views/SettingsView';
import FormsView from './views/FormsView';

/**
 * Routing exists for one hard requirement: the QR check-in link has to survive
 * a cold page load, which the previous state-based navigation could not do.
 * Paths are Spanish to match the UI and the printed QR codes.
 */

/** Standalone message screen for states where no section can be shown. */
const Notice = ({ tone, title, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
    <div className="w-full max-w-md space-y-4">
      <h1 className="text-center text-2xl font-bold text-indigo-900">{t('common.appName')}</h1>
      <Alert tone={tone} title={title}>{children}</Alert>
      <button
        onClick={() => signOut().then(() => window.location.assign('/ingresar'))}
        className="w-full rounded-lg border border-slate-300 bg-white py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        {t('auth.logout')}
      </button>
    </div>
  </div>
);

const RequireAuth = ({ children, allow }) => {
  const { loading, role, blocked, authenticated, pendingApproval, rejected } = useSession();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;

  if (!role) {
    if (blocked) {
      return (
        <Notice tone="danger" title={t('auth.blockedTitle')}>{t('auth.blockedBody')}</Notice>
      );
    }
    if (pendingApproval) {
      return (
        <Notice tone="info" title={t('professionalSignup.pendingTitle')}>{t('professionalSignup.pendingBody')}</Notice>
      );
    }
    if (rejected) {
      return (
        <Notice tone="danger" title={t('professionalSignup.rejectedTitle')}>{t('professionalSignup.rejectedBody')}</Notice>
      );
    }
    // Signed in with no profile: an invited professional whose record admin has
    // not created yet, or someone who signed up without being expected.
    // Bouncing them to the login page is baffling — they are logged in.
    if (authenticated) {
      return (
        <Notice tone="warning" title={t('auth.noProfileTitle')}>{t('auth.noProfileBody')}</Notice>
      );
    }
    return <Navigate to="/ingresar" state={{ from: location.pathname }} replace />;
  }

  if (allow && !allow.includes(role)) return <Navigate to="/" replace />;

  return <Layout>{children}</Layout>;
};

const RedirectIfAuthed = ({ children }) => {
  const { loading, role } = useSession();
  if (loading) return <FullPageSpinner />;
  if (role) return <Navigate to="/" replace />;
  return children;
};

/**
 * Patients keep Mi perfil. A professional's own settings are Configuración's
 * first tab, so an old link or a habit lands there.
 */
const ProfileRoute = () => {
  const { isPatient } = useSession();
  return isPatient ? <ProfileView /> : <Navigate to="/configuracion" replace />;
};

const AppRoutes = () => (
  <Routes>
    <Route path="/ingresar" element={<RedirectIfAuthed><LoginView /></RedirectIfAuthed>} />
    <Route path="/activar" element={<RedirectIfAuthed><ActivateProfessionalView /></RedirectIfAuthed>} />
    <Route path="/recuperar" element={<RedirectIfAuthed><ForgotPasswordView /></RedirectIfAuthed>} />
    {/* NOT behind RedirectIfAuthed: the recovery link signs the user in, so a
        redirect for signed-in users would bounce them off this very page. */}
    <Route path="/nueva-clave" element={<ResetPasswordView />} />
    <Route path="/registro-profesional" element={<RedirectIfAuthed><ProfessionalSignupView /></RedirectIfAuthed>} />
    <Route path="/registro" element={<RedirectIfAuthed><RegisterView /></RedirectIfAuthed>} />

    {/* QR destination: any signed-in role, patient or professional */}
    <Route path="/checkin/:token" element={<RequireAuth><CheckInView /></RequireAuth>} />

    <Route path="/" element={<RequireAuth><HomeView /></RequireAuth>} />

    <Route path="/reservar" element={<RequireAuth><BookingView /></RequireAuth>} />
    <Route path="/mi-perfil" element={<RequireAuth><ProfileRoute /></RequireAuth>} />
    <Route path="/mis-pagos" element={
      <RequireAuth allow={['patient']}><MyPaymentsView /></RequireAuth>
    } />

    <Route path="/agenda" element={
      <RequireAuth allow={['admin', 'therapist']}><AgendaView /></RequireAuth>
    } />
    <Route path="/historia-clinica" element={
      <RequireAuth allow={['admin', 'therapist']}><ClinicalHistoryView /></RequireAuth>
    } />

    <Route path="/pacientes" element={
      <RequireAuth allow={['admin', 'therapist']}><PatientsView /></RequireAuth>
    } />
    <Route path="/solicitudes" element={
      <RequireAuth allow={['admin', 'therapist']}><ApprovalsView /></RequireAuth>
    } />
    <Route path="/pagos" element={
      <RequireAuth allow={['admin', 'therapist']}><PaymentsView /></RequireAuth>
    } />
    <Route path="/pagos/facturacion" element={
      <RequireAuth allow={['admin', 'therapist']}><BillingSummaryView /></RequireAuth>
    } />
    <Route path="/caja" element={
      <RequireAuth allow={['admin', 'therapist']}><CashFlowView /></RequireAuth>
    } />
    <Route path="/profesionales" element={
      <RequireAuth allow={['admin']}><ProfessionalsView /></RequireAuth>
    } />
    <Route path="/sedes" element={
      <RequireAuth allow={['admin']}><LocationsView /></RequireAuth>
    } />
    <Route path="/datos-maestros" element={
      <RequireAuth allow={['admin', 'therapist']}><MasterDataView /></RequireAuth>
    } />
    <Route path="/suscripciones" element={
      <RequireAuth allow={['admin']}><SubscriptionsView /></RequireAuth>
    } />
    {/* A professional's own settings, and for admin the practice's too. */}
    <Route path="/configuracion" element={
      <RequireAuth allow={['admin', 'therapist']}><SettingsView /></RequireAuth>
    } />
    <Route path="/configuracion/:tab" element={
      <RequireAuth allow={['admin', 'therapist']}><SettingsView /></RequireAuth>
    } />
    <Route path="/formularios" element={
      <RequireAuth allow={['admin', 'therapist']}><FormsView /></RequireAuth>
    } />

    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
);

const App = () => (
  <BrowserRouter>
    <SessionProvider>
      <AppRoutes />
    </SessionProvider>
  </BrowserRouter>
);

export default App;
